import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import {AbstractControl, FormControl, FormGroup, Validators} from '@angular/forms';
import {Zone} from '../../zone/zone';
import {Thuisverzorger} from '../thuisverzorger';
import {Diensten} from '../../dienst/diensten';
import {ZahValidators} from '../../core/form/validators';
import {Dienst} from "../../dienst/dienst";
import {firstValueFrom} from "rxjs";
import {DialogService} from "../../core/dialog/dialog.service";
import {ThuisverzorgerDienst} from "../thuisverzorger-dienst";

@Component({
    selector: 'zah-upsert-thuisverzorger',
    templateUrl: './upsert-thuisverzorger.component.html',
    styleUrls: ['./upsert-thuisverzorger.component.scss'],
    standalone: false
})
export class UpsertThuisverzorgerComponent implements OnInit {
  private dialogService = inject(DialogService);

  @Input()
  thuisverzorger?: Thuisverzorger;
  @Input()
  okLabel: string;
  @Input()
  zones: Array<Zone>;
  @Input()
  diensten!: Diensten;
  @Output()
  cancelClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  okClicked: EventEmitter<Thuisverzorger> = new EventEmitter<Thuisverzorger>();
  @Output()
  editDienstenClicked: EventEmitter<void> = new EventEmitter<void>();

  upsertThuisverzorgerForm: FormGroup;

  ngOnInit(): void {
    this.upsertThuisverzorgerForm = new FormGroup({
      voornaam: new FormControl(this.thuisverzorger?.getVoornaam(), ZahValidators.notBlank),
      achternaam: new FormControl(this.thuisverzorger?.getAchternaam(), ZahValidators.notBlank),
      personeelsnummer: new FormControl(
        {value: this.thuisverzorger?.getPersoneelsnummer(), disabled: !!this.thuisverzorger},
        ZahValidators.notBlank),
      email: new FormControl(
        {value: this.thuisverzorger?.getEmail(), disabled: !!this.thuisverzorger},
        [ZahValidators.notBlank, Validators.email]),
      diensten: new FormControl(this.thuisverzorger?.getDiensten() ?? [], ZahValidators.notInvalidDiensten),
    });
  }

  onCancelClicked(): void {
    this.cancelClicked.emit();
  }

  onOkClicked(): void {
    this.okClicked.emit(new Thuisverzorger(
      this.thuisverzorger?.getId(),
      this.voornaam.value,
      this.achternaam.value,
      this.personeelsnummer.value,
      this.email.value,
      this.selectedDiensten.value
      )
    );
  }

  get voornaam(): AbstractControl {
    return this.upsertThuisverzorgerForm.controls.voornaam;
  }

  get achternaam(): AbstractControl {
    return this.upsertThuisverzorgerForm.controls.achternaam;
  }

  get personeelsnummer(): AbstractControl {
    return this.upsertThuisverzorgerForm.controls.personeelsnummer;
  }

  get email(): AbstractControl {
    return this.upsertThuisverzorgerForm.controls.email;
  }

  get selectedDiensten(): AbstractControl {
    return this.upsertThuisverzorgerForm.controls.diensten;
  }

  getDienst(dienstId: string): Dienst {
    return this.diensten.getDienst(dienstId);
  }

  async onAddDienstClick(): Promise<void> {
    const dienst = await firstValueFrom(
      this.dialogService.askSelection({
        message: 'Kies de dienst die je wil toevoegen:',
        optionList: this.getAvailableDiensten(),
        defaultValue: this.getAvailableDiensten()[0],
        confirmLabel: 'Voeg toe'
      })
    );

    this.upsertThuisverzorgerForm.controls.diensten.setValue([...this.getThuisVerzorgerDiensten(), new ThuisverzorgerDienst(dienst.getId(), [])]);
  }

  getAvailableDiensten(): Array<Dienst> {
    return this.diensten.getDiensten().filter(dienst => !this.getThuisVerzorgerDiensten().some(thuisverzorgerDienst => thuisverzorgerDienst.getId() === dienst.getId()));
  }

  getThuisVerzorgerDiensten(): Array<ThuisverzorgerDienst> {
    return this.upsertThuisverzorgerForm.controls.diensten.value;
  }

  onDienstDeleteClick(dienstId: ThuisverzorgerDienst) {
    const diensten = this.upsertThuisverzorgerForm.controls.diensten.value;
    diensten.splice(diensten.indexOf(dienstId), 1)

    this.upsertThuisverzorgerForm.controls.diensten.setValue(diensten);
  }

  getSelectedDienstNames(): string {
    return this.getThuisVerzorgerDiensten().map(dienst => this.getDienst(dienst.getId()).getLabel()).join(', ');
  }

  // TODO get rid of setter
  onZoneIdValueChanged(thuisverzorgerDienst: ThuisverzorgerDienst, zoneIds: string[]) {
    thuisverzorgerDienst.setZoneIds(zoneIds);
    this.upsertThuisverzorgerForm.controls.diensten.updateValueAndValidity();
  }
}
